import type { openai } from '@ai-sdk/openai';
import type { novaToolDefs, outlookToolDefs } from '@newfront-insurance/ai-api';
import type { ToolUIPart } from 'ai';
import type { InferUITool } from 'ai';
import { useState } from 'react';

import { formatSkillCommand } from '../../../lib/skill-command-utils';
import {
  ChainOfThoughtSearchResult,
  ChainOfThoughtSearchResults,
  CodeBlock,
  Sandbox,
  SandboxContent,
  SandboxHeader,
  SandboxTabContent,
  SandboxTabs,
  SandboxTabsBar,
  SandboxTabsList,
  SandboxTabsTrigger,
} from '../../ai-elements';
import { SourceBadge } from '../../ai-elements/source-badge';
import { useSkillsSafe } from '../contexts/skills-context';
import type { CoTStep } from '../lib/chain-of-thought-utils';
import { type CoTSearchResult, getField, MAX_OUTPUT_LENGTH } from '../lib/chain-of-thought-utils';
import type { SourcePart, SourcesMap } from '../lib/sources';

type ToolIO<T extends keyof typeof novaToolDefs> = InferUITool<(typeof novaToolDefs)[T]>;
type OutlookToolIO<T extends keyof typeof outlookToolDefs> = InferUITool<(typeof outlookToolDefs)[T]>;
type ShellToolIO = InferUITool<ReturnType<typeof openai.tools.shell>>;
type DataExplorerOutputWithSources = ToolIO<'dataExplorer'>['output'] & {
  sources?: Array<{
    sourceId: string;
    url: string;
  }>;
};

type NovaToolInputUnion = {
  [K in keyof typeof novaToolDefs]: { toolName: K; input: ToolIO<K>['input'] };
}[keyof typeof novaToolDefs];
type OutlookToolInputUnion = {
  [K in keyof typeof outlookToolDefs]: { toolName: K; input: OutlookToolIO<K>['input'] };
}[keyof typeof outlookToolDefs];
type ProviderToolInputUnion = { toolName: string; input: unknown };
export type CoTToolInputProps = (NovaToolInputUnion | OutlookToolInputUnion | ProviderToolInputUnion) & {
  priorSteps?: CoTStep[];
};

type ToolContext = {
  filesById: Map<string, { filename: string; link?: string }>;
};

function buildToolContext(priorSteps: CoTStep[] = []): ToolContext {
  const filesById = new Map<string, { filename: string; link?: string }>();

  for (const step of priorSteps) {
    if (step.type !== 'tool' || !step.output) continue;

    if (step.toolName === 'documentSearch') {
      const { results } = step.output as ToolIO<'documentSearch'>['output'];
      if (!Array.isArray(results)) continue;
      results.forEach((result) => {
        filesById.set(result.fileId, { filename: result.filename, link: result.navigatorUrl });
      });
    }

    if (step.toolName === 'listFiles') {
      const { files } = step.output as ToolIO<'listFiles'>['output'];
      if (!Array.isArray(files)) continue;
      files.forEach((file) => {
        filesById.set(file.fileId, { filename: file.fileName, link: file.navigatorUrl });
      });
    }
  }

  return { filesById };
}

function getReadFileReference(input: unknown, priorSteps?: CoTStep[]) {
  const fileId = getField<string>(input, 'fileId');
  if (!fileId) return undefined;
  return buildToolContext(priorSteps).filesById.get(fileId);
}

export function hasReadFileReference(input: unknown, priorSteps?: CoTStep[]) {
  return !!getReadFileReference(input, priorSteps);
}

export function CoTReadFileTitle({ input, priorSteps }: { input: unknown; priorSteps?: CoTStep[] }) {
  const file = getReadFileReference(input, priorSteps);
  if (!file) return null;

  const pages = getField<string>(input, 'pages');
  const pagesLabel = pages ? ` (pages ${pages})` : '';

  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span>Read</span>
      <ChainOfThoughtSearchResult className="max-w-64 truncate">
        {file.filename}
        {pagesLabel}
      </ChainOfThoughtSearchResult>
    </span>
  );
}

const READ_FILE_PREVIEW_MAX_CHARS = 280;
const READ_FILE_PREVIEW_MIN_CHARS = 24;

function getShellCommandText(input: unknown) {
  return getField<string[]>(input, 'action', 'commands')?.join('\n') ?? '';
}

function getShellOutputText(output: unknown) {
  const shellOutput = output as ShellToolIO['output'];
  if (!shellOutput?.output?.length) return '';
  return shellOutput.output
    .map((entry) => [entry.stdout, entry.stderr].filter(Boolean).join(''))
    .filter(Boolean)
    .join('\n')
    .trim();
}

function ShellCodePanel({ language, text }: { language: string; text: string }) {
  return (
    <CodeBlock
      className={[
        'bg-background/60 rounded-none border-0',
        '[&_pre]:!max-h-64 [&_pre]:!overflow-auto [&_pre]:!p-3 [&_pre]:!py-2',
        '[&_pre]:!text-[11px] [&_pre]:!leading-5',
        '[&_pre_code]:!text-[11px] [&_pre_code]:!leading-5',
      ].join(' ')}
      code={text}
      language={language}
    />
  );
}

function CoTShellSandbox({ input, output, state }: { input: unknown; output?: unknown; state: ToolUIPart['state'] }) {
  const commands = getShellCommandText(input);
  const outputText = output ? getShellOutputText(output) : '';

  return (
    <Sandbox className="my-0" data-testid="chain-of-thought-shell-sandbox">
      <SandboxHeader data-testid="chain-of-thought-shell-sandbox-header" state={state} title="Shell" />
      <SandboxContent>
        <SandboxTabs defaultValue={output ? 'output' : 'code'}>
          <SandboxTabsBar>
            <SandboxTabsList>
              <SandboxTabsTrigger aria-label="View command" data-testid="chain-of-thought-shell-code-tab" value="code">
                Code
              </SandboxTabsTrigger>
              <SandboxTabsTrigger
                aria-label="View command output"
                data-testid="chain-of-thought-shell-output-tab"
                disabled={!output}
                value="output"
              >
                Output
              </SandboxTabsTrigger>
            </SandboxTabsList>
          </SandboxTabsBar>
          <SandboxTabContent value="code">
            <ShellCodePanel language="bash" text={commands || '# Waiting for command...'} />
          </SandboxTabContent>
          <SandboxTabContent value="output">
            <ShellCodePanel language="log" text={outputText || 'No output'} />
          </SandboxTabContent>
        </SandboxTabs>
      </SandboxContent>
    </Sandbox>
  );
}

function TruncatedText({ text, maxWords }: { text: string; maxWords: number }) {
  const [expanded, setExpanded] = useState(false);
  const words = text.split(/\s+/);
  const isTruncated = words.length > maxWords;

  if (!isTruncated) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {expanded ? text : `${words.slice(0, maxWords).join(' ')} [...]`}{' '}
      <button
        type="button"
        className="text-primary cursor-pointer text-xs hover:underline"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'show less' : 'see more'}
      </button>
    </span>
  );
}

function formatCount(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

function getReadFileStatLine(output: unknown, content: string) {
  const returnedPages = getField<number>(output, 'returnedPages');
  const totalPages = getField<number>(output, 'totalPages');
  const pageCount = returnedPages ?? totalPages;
  const characterLabel = `${formatCount(content.length)} ${pluralize(content.length, 'character')}`;

  if (!pageCount) {
    return `Read ${characterLabel}`;
  }

  return `Read ${formatCount(pageCount)} ${pluralize(pageCount, 'page')}, ${characterLabel}`;
}

function cleanReadFilePreview(content: string) {
  const cleaned = content
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*]\((?:https?:\/\/|mailto:)[^)]+\)/gi, ' ')
    .replace(/<https?:\/\/[^>]+>/gi, ' ')
    .replace(/<mailto:[^>]+>/gi, ' ')
    .replace(/\bcid:[^\s>\]]+/gi, ' ')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/mailto:\S+/gi, ' ')
    .replace(/[#*_`>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (cleaned.length < READ_FILE_PREVIEW_MIN_CHARS) {
    return null;
  }

  const tokens = cleaned.split(/\s+/);
  const longTokenCount = tokens.filter((token) => token.length > 48).length;
  if (tokens.length > 0 && longTokenCount / tokens.length > 0.25) {
    return null;
  }

  const preview = cleaned.slice(0, READ_FILE_PREVIEW_MAX_CHARS).trim();
  if (preview.length < READ_FILE_PREVIEW_MIN_CHARS) {
    return null;
  }

  return preview.length < cleaned.length ? `${preview.replace(/[,.:\s]+$/, '')}...` : preview;
}

function ReadFilePreview({ output }: { output: unknown }) {
  const error = getField<string>(output, 'error');
  if (error) return <p>{error}</p>;

  const content = getField<string>(output, 'content');
  if (!content) return null;

  const preview = cleanReadFilePreview(content);

  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs">{getReadFileStatLine(output, content)}</p>
      {preview && (
        <blockquote className="border-border/80 text-muted-foreground max-h-24 overflow-hidden border-l-2 pl-3 text-sm leading-6">
          &ldquo;{preview}&rdquo;
        </blockquote>
      )}
    </div>
  );
}

function LoadSkillDescription({ output }: { output: unknown }) {
  const skillsContext = useSkillsSafe();
  const command = getField<string>(output, 'command');
  const skillId = getField<string>(output, 'skillId') ?? getField<string>(output, 'id');
  const name = getField<string>(output, 'name');
  const description = skillsContext?.getSkillDescription({
    command,
    skillId,
    name,
  });

  if (description) {
    return <p>{description}</p>;
  }

  if (name) {
    return <p>{name}</p>;
  }

  return command ? <p>{formatSkillCommand(command)}</p> : null;
}

export function hasCoTToolInputContent({ toolName, input, priorSteps }: CoTToolInputProps): boolean {
  if (toolName === 'shell') return !!getShellCommandText(input);

  switch (toolName) {
    case 'documentSearch':
      return !!getField<string>(input, 'query');
    case 'listFiles': {
      const filters = [
        getField<string>(input, 'filenameSearch'),
        getField<unknown[]>(input, 'categories')?.length,
        getField<unknown>(input, 'objectReference'),
        getField<string>(input, 'uploadedAfter'),
        getField<string>(input, 'uploadedBefore'),
      ];
      return filters.some(Boolean);
    }
    case 'readFile':
      return !getReadFileReference(input, priorSteps);
    case 'processFiles':
      return !!getField<string>(input, 'context');
    case 'dataExplorer':
      return !!getField<string>(input, 'lastMessageReasoning');
    case 'getMessage':
      return getField<string>(input, 'source', 'type') === 'mailbox' && !!getField<string>(input, 'source', 'mailbox');
    case 'downloadEmailAttachments':
    case 'exposeFileToUser':
    case 'stageFileForShell':
      return true;
    case 'listInbox':
    case 'listUnifiedMail':
    case 'loadSkill':
      return false;
    default:
      return false;
  }
}

/** Renders tool input as a description for a CoT step — returns null when no custom renderer exists */
export function CoTToolInput({ toolName, input, priorSteps }: CoTToolInputProps) {
  // Shell tool is an OpenAI provider tool (not in novaToolDefs/outlookToolDefs)
  if (toolName === ('shell' as string)) {
    return getShellCommandText(input) ? <CoTShellSandbox input={input} state="input-available" /> : null;
  }

  switch (toolName) {
    case 'documentSearch': {
      const query = getField<string>(input, 'query');
      return query ? <span>Searching for &ldquo;{query}&rdquo;</span> : null;
    }
    case 'listFiles': {
      const { categories, filenameSearch, objectReference, uploadedAfter, uploadedBefore } =
        input as ToolIO<'listFiles'>['input'];
      const filters = [
        filenameSearch ? `matching "${filenameSearch}"` : null,
        categories?.length ? `in ${categories.join(', ')}` : null,
        objectReference ? `for ${objectReference.entityType}` : null,
        uploadedAfter ? `uploaded after ${uploadedAfter}` : null,
        uploadedBefore ? `uploaded before ${uploadedBefore}` : null,
      ].filter(Boolean);

      return <span>{filters.length ? `Searching files ${filters.join(' ')}` : 'Searching files'}</span>;
    }
    case 'readFile': {
      const pages = getField<string>(input, 'pages');
      const file = getReadFileReference(input, priorSteps);
      const pagesLabel = pages ? ` (pages ${pages})` : '';
      if (file) {
        return null;
      }
      return <span>Reading file{pagesLabel}</span>;
    }
    case 'processFiles': {
      const context = getField<string>(input, 'context');
      if (!context) return null;
      return <TruncatedText text={context} maxWords={100} />;
    }
    case 'dataExplorer': {
      const lastMessageReasoning = getField<string>(input, 'lastMessageReasoning');
      return lastMessageReasoning ? <span>{lastMessageReasoning}</span> : null;
    }
    case 'getMessage': {
      const source = getField<{ type?: string; mailbox?: string }>(input, 'source');
      if (source?.type === 'mailbox' && source.mailbox) {
        return <span>Reading message from {source.mailbox}</span>;
      }
      return null;
    }
    case 'downloadEmailAttachments': {
      const attachmentNames = getField<string[]>(input, 'attachmentNames');
      if (attachmentNames?.length) {
        return <span>Downloading {attachmentNames.join(', ')}</span>;
      }
      return <span>Downloading all attachments</span>;
    }
    // listInbox / listUnifiedMail: progress descriptions arrive via stepProgressMap,
    // so we only render input details that won't duplicate the progress message.
    case 'listInbox':
    case 'listUnifiedMail':
    case 'loadSkill':
      return null;
    case 'exposeFileToUser': {
      const { filename, kind } = input as ToolIO<'exposeFileToUser'>['input'] & { filename?: string };
      if (filename) {
        return <span>{kind === 'skill' ? 'Preparing skill bundle' : 'Preparing download'}</span>;
      }
      return <span>{kind === 'skill' ? 'Preparing skill bundle' : 'Preparing download'}</span>;
    }
    case 'stageFileForShell': {
      return <span>Preparing file for review...</span>;
    }
    default:
      return null;
  }
}

/** Renders search result badges from tool output — returns null when no extractor exists or no results */
export function CoTToolSearchResults({
  toolName,
  output,
  sourcesMap,
}: {
  toolName: string;
  output: unknown;
  sourcesMap?: SourcesMap;
}) {
  // Data explorer: render source badges + remaining count badge
  if (toolName === 'dataExplorer') {
    return <DataExplorerCoTSearchResults output={output} sourcesMap={sourcesMap} />;
  }

  const results = extractSearchResults(toolName, output);
  if (results.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {results.map((result, index) =>
        result.link ? (
          <a key={result.link} href={result.link} target="_blank" rel="noopener noreferrer">
            <ChainOfThoughtSearchResult>{result.title}</ChainOfThoughtSearchResult>
          </a>
        ) : (
          <ChainOfThoughtSearchResult key={index}>{result.title}</ChainOfThoughtSearchResult>
        ),
      )}
    </ChainOfThoughtSearchResults>
  );
}

function DataExplorerCoTSearchResults({ output, sourcesMap }: { output: unknown; sourcesMap?: SourcesMap }) {
  const { data, sources } = output as DataExplorerOutputWithSources;

  if (!sources?.length && (!data || data.rowCount === 0)) return null;

  const sourceIndexMap = new Map(Array.from(sourcesMap?.keys() ?? []).map((id, i) => [id, i]));

  const matchedSources = (sources ?? [])
    .map((s: NonNullable<DataExplorerOutputWithSources['sources']>[number]) => {
      const source = sourcesMap?.get(s.sourceId);
      if (!source) return null;
      const globalIndex = sourceIndexMap.get(s.sourceId) ?? 0;
      return { source, index: globalIndex };
    })
    .filter((s): s is { source: SourcePart; index: number } => s !== null);

  const totalResults = data?.rowCount ?? 0;
  const remainingCount = totalResults - matchedSources.length;

  if (matchedSources.length === 0 && totalResults > 0) {
    return (
      <ChainOfThoughtSearchResults>
        <ChainOfThoughtSearchResult>
          {totalResults} result{totalResults === 1 ? '' : 's'} found
        </ChainOfThoughtSearchResult>
      </ChainOfThoughtSearchResults>
    );
  }

  if (matchedSources.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {matchedSources.map(({ source, index }) => (
        <SourceBadge key={source.sourceId} source={source} index={index} size="sm" />
      ))}
      {remainingCount > 0 && (
        <ChainOfThoughtSearchResult>
          {remainingCount} other result{remainingCount === 1 ? '' : 's'}
        </ChainOfThoughtSearchResult>
      )}
    </ChainOfThoughtSearchResults>
  );
}

/** Renders tool output in a CoT step — uses custom renderer if available, falls back to truncated string */
export function CoTToolOutput({ toolName, input, output }: { toolName: string; input?: unknown; output: unknown }) {
  switch (toolName) {
    case 'documentSearch': {
      const { searchSummary } = output as ToolIO<'documentSearch'>['output'];
      return searchSummary ? <p>{searchSummary}</p> : null;
    }
    case 'listFiles': {
      const { files, totalFiles } = output as ToolIO<'listFiles'>['output'];
      if (totalFiles === 0) return <p>No files found</p>;
      if (files.length < totalFiles) {
        return (
          <p>
            Showing {files.length} of {totalFiles} files
          </p>
        );
      }
      return (
        <p>
          Found {totalFiles} file{totalFiles === 1 ? '' : 's'}
        </p>
      );
    }
    case 'readFile': {
      return <ReadFilePreview output={output} />;
    }
    case 'getMessage': {
      const { message, error } = output as OutlookToolIO<'getMessage'>['output'];
      if (error) return <p>{error}</p>;
      if (message?.subject) return <p>{message.subject}</p>;
      return null;
    }
    case 'downloadEmailAttachments': {
      const { downloadedFiles, unsupportedFiles, error } =
        output as OutlookToolIO<'downloadEmailAttachments'>['output'];
      if (error) return <p>{error}</p>;
      const downloaded = downloadedFiles ?? [];
      const unsupported = unsupportedFiles ?? [];
      if (downloaded.length === 0 && unsupported.length === 0) return null;
      return (
        <p>
          {downloaded.length > 0 && <>Downloaded {downloaded.map((f) => f.filename).join(', ')}</>}
          {unsupported.length > 0 && (
            <>
              {downloaded.length > 0 && <br />}
              {unsupported.length} unsupported file{unsupported.length === 1 ? '' : 's'}
            </>
          )}
        </p>
      );
    }
    case 'listInbox': {
      const { messages, error } = output as OutlookToolIO<'listInbox'>['output'];
      if (error) return <p>{error}</p>;
      const count = messages?.length ?? 0;
      if (count === 0) return <p>No messages found</p>;
      return (
        <p>
          Found {count} message{count === 1 ? '' : 's'}
        </p>
      );
    }
    case 'listUnifiedMail': {
      const { itemsBySource, error } = output as OutlookToolIO<'listUnifiedMail'>['output'];
      if (error) return <p>{error}</p>;
      const count = itemsBySource?.reduce((sum, source) => sum + (source.items?.length ?? 0), 0) ?? 0;
      if (count === 0) return <p>No messages found</p>;
      return (
        <p>
          Found {count} message{count === 1 ? '' : 's'}
        </p>
      );
    }
    case 'loadSkill': {
      return <LoadSkillDescription output={output} />;
    }
    case 'shell': {
      return <CoTShellSandbox input={input} output={output} state="output-available" />;
    }
    case 'exposeFileToUser': {
      const { kind } = output as ToolIO<'exposeFileToUser'>['output'];
      return <p>{kind === 'skill' ? 'Ready to save as a skill' : 'Ready to download'}</p>;
    }
    case 'stageFileForShell': {
      return <p>File ready for Nova</p>;
    }
    case 'dataExplorer': {
      const { reasoning } = output as ToolIO<'dataExplorer'>['output'];
      return reasoning ? <p>{reasoning}</p> : null;
    }
    default: {
      if (typeof output === 'string') {
        const text = output.length > MAX_OUTPUT_LENGTH ? `${output.slice(0, MAX_OUTPUT_LENGTH)}…` : output;
        return <p>{text}</p>;
      }
      return null;
    }
  }
}

export function hasCoTToolOutputContent({
  toolName,
  input,
  output,
}: {
  toolName: string;
  input?: unknown;
  output: unknown;
}): boolean {
  if (extractSearchResults(toolName, output).length > 0) return true;

  switch (toolName) {
    case 'dataExplorer':
      return (
        !!getField<string>(output, 'reasoning') ||
        !!getField<unknown[]>(output, 'sources')?.length ||
        !!getField<number>(output, 'data', 'rowCount')
      );
    case 'documentSearch':
      return !!getField<string>(output, 'searchSummary');
    case 'listFiles':
      return getField<number>(output, 'totalFiles') != null;
    case 'readFile':
      return !!getField<string>(output, 'error') || !!getField<string>(output, 'content');
    case 'getMessage':
      return !!getField<string>(output, 'error') || !!getField<string>(output, 'message', 'subject');
    case 'downloadEmailAttachments':
      return (
        !!getField<string>(output, 'error') ||
        !!getField<unknown[]>(output, 'downloadedFiles')?.length ||
        !!getField<unknown[]>(output, 'unsupportedFiles')?.length
      );
    case 'listInbox':
      return !!getField<string>(output, 'error') || Array.isArray(getField<unknown[]>(output, 'messages'));
    case 'listUnifiedMail':
      return !!getField<string>(output, 'error') || Array.isArray(getField<unknown[]>(output, 'itemsBySource'));
    case 'loadSkill':
      return !!(
        getField<string>(output, 'command') ||
        getField<string>(output, 'skillId') ||
        getField<string>(output, 'id') ||
        getField<string>(output, 'name')
      );
    case 'shell':
      return !!getShellOutputText(output);
    case 'exposeFileToUser':
      return !!(getField<string>(output, 'filename') || getField<string>(input, 'filename'));
    case 'stageFileForShell':
      return true;
    default:
      return typeof output === 'string' && output.length > 0;
  }
}

function extractSearchResults(toolName: string, output: unknown): CoTSearchResult[] {
  switch (toolName) {
    case 'documentSearch': {
      const { results } = output as ToolIO<'documentSearch'>['output'];
      if (!Array.isArray(results)) return [];
      return results
        .filter((r) => typeof r.filename === 'string')
        .map((r) => ({ title: r.filename, link: r.navigatorUrl }));
    }
    case 'listFiles': {
      const { files } = output as ToolIO<'listFiles'>['output'];
      if (!Array.isArray(files)) return [];
      return files
        .filter((file) => typeof file.fileName === 'string')
        .map((file) => ({ title: file.fileName, link: file.navigatorUrl }));
    }
    case 'loadSkill': {
      return [];
    }
    default:
      return [];
  }
}
